import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Banknote, Copy, ArrowRight, AlertCircle, ChevronRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Alert, AlertDescription } from "../components/ui/alert";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
  { id: 1, label: "Register for Course" },
  { id: 2, label: "Complete Bank Transfer" },
  { id: 3, label: "Submit Proof of Payment" },
  { id: 4, label: "Admin Verifies Payment" },
  { id: 5, label: "Enrollment Activated" },
];

function statusToStep(status) {
  if (status === "pending_bank_transfer" || status === "needs_clarification") return 2;
  if (status === "proof_submitted") return 4;
  if (status === "paid") return 5;
  return 1;
}

const BankTransferConfirmation = () => {
  const { orderId } = useParams();
  const [params] = useSearchParams();
  const token = params.get("token") || "";

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/orders/${orderId}`, { params: { token } });
        setOrder(res.data);
      } catch (err) {
        setError(err.response?.data?.detail || "Could not load order");
      } finally {
        setLoading(false);
      }
    })();
  }, [orderId, token]);

  const copy = (val, label = "Copied") => {
    navigator.clipboard.writeText(val);
    toast.success(`${label}: ${val}`);
  };

  if (loading) {
    return <div className="min-h-screen bg-slate-50 p-8"><div className="container mx-auto max-w-4xl animate-pulse"><div className="h-96 skeleton rounded-xl" /></div></div>;
  }
  if (error || !order) {
    return (
      <div className="min-h-screen bg-slate-50 p-8">
        <div className="container mx-auto max-w-3xl">
          <Alert variant="destructive"><AlertCircle className="h-4 w-4" /><AlertDescription>{error || "Order not found"}</AlertDescription></Alert>
        </div>
      </div>
    );
  }

  const bd = order.bank_details || {};
  const orderShort = `#${order.id.slice(0, 8).toUpperCase()}`;
  const currentStep = statusToStep(order.status);

  return (
    <div className="min-h-screen bg-slate-50 py-10">
      <div className="container mx-auto px-4 lg:px-8 max-w-4xl space-y-8">
        <div className="text-center">
          <div className="w-20 h-20 bg-brand/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="w-10 h-10 text-brand" />
          </div>
          <h1 className="font-heading font-bold text-3xl text-primary mb-2" data-testid="bt-confirm-title">Thank you — your order is confirmed</h1>
          <p className="text-muted-foreground">Please complete your bank transfer to activate your enrollment.</p>
        </div>

        {/* Step indicator */}
        <Card>
          <CardContent className="p-6">
            <ol className="grid grid-cols-1 md:grid-cols-5 gap-3" data-testid="status-steps">
              {STEPS.map((s) => (
                <li key={s.id} className={`flex items-center gap-2 p-3 rounded-md border ${
                  s.id < currentStep ? "bg-green-50 border-green-200 text-green-800" :
                  s.id === currentStep ? "bg-brand/5 border-brand text-brand font-semibold" :
                  "bg-slate-50 border-slate-200 text-slate-500"}`}>
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs flex-shrink-0 ${
                    s.id < currentStep ? "bg-green-600 text-white" :
                    s.id === currentStep ? "bg-brand text-white" :
                    "bg-slate-300 text-white"}`}>
                    {s.id < currentStep ? <CheckCircle2 className="w-4 h-4" /> : s.id}
                  </span>
                  <span className="text-xs md:text-sm">{s.label}</span>
                </li>
              ))}
            </ol>
          </CardContent>
        </Card>

        {/* Order summary */}
        <Card data-testid="bt-order-summary">
          <CardHeader>
            <CardTitle className="font-heading flex items-center justify-between">
              <span>Order {orderShort}</span>
              <Badge className="bg-yellow-100 text-yellow-800">{order.status?.replace(/_/g, " ")}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="grid md:grid-cols-2 gap-4">
            <div><p className="text-xs text-muted-foreground">Course</p><p className="font-medium">{order.course_title}</p></div>
            <div><p className="text-xs text-muted-foreground">Customer</p><p className="font-medium">{order.customer_name}</p></div>
            <div><p className="text-xs text-muted-foreground">Email</p><p className="font-medium">{order.customer_email}</p></div>
            <div><p className="text-xs text-muted-foreground">Amount Due</p><p className="font-bold text-2xl text-brand">${(order.amount || 0).toFixed(2)} USD</p></div>
          </CardContent>
        </Card>

        {/* Bank options */}
        <Card>
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <Banknote className="h-5 w-5 text-brand" /> Bank Transfer Details
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-1">Use any of the accounts below (Jamaica USD, Jamaica JMD, or United States USD). <strong>Use Order {orderShort} as your payment reference.</strong></p>
          </CardHeader>
          <CardContent className="space-y-6">
            {bd.usd && (
              <div className="border rounded-lg p-5 bg-white" data-testid="bank-usd">
                <div className="flex justify-between items-start mb-3">
                  <h3 className="font-semibold text-lg">Pay in USD</h3>
                  <Badge className="bg-blue-100 text-blue-800">USD ${(bd.amount_usd || order.amount).toFixed(2)}</Badge>
                </div>
                <BankRow label="Bank Name" value={bd.usd.bank_name} />
                <BankRow label="Account Name" value={bd.usd.account_name} />
                <BankRow label="Account Number" value={bd.usd.account_number} onCopy={copy} />
                <BankRow label="Routing / SWIFT" value={bd.usd.routing_swift} />
                <BankRow label="Reference" value={`Order ${orderShort}`} onCopy={copy} highlight />
              </div>
            )}
            {bd.jmd && (
              <div className="border rounded-lg p-5 bg-white" data-testid="bank-jmd">
                <div className="flex justify-between items-start mb-3">
                  <h3 className="font-semibold text-lg">Pay in JMD</h3>
                  <Badge className="bg-emerald-100 text-emerald-800">JMD {Number(bd.amount_jmd || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</Badge>
                </div>
                <BankRow label="Bank Name" value={bd.jmd.bank_name} />
                <BankRow label="Account Name" value={bd.jmd.account_name} />
                <BankRow label="Account Number" value={bd.jmd.account_number} onCopy={copy} />
                <BankRow label="Routing / SWIFT" value={bd.jmd.routing_swift} />
                <BankRow label="Reference" value={`Order ${orderShort}`} onCopy={copy} highlight />
                <p className="text-xs text-muted-foreground mt-2">Converted at JMD {bd.rate_jmd_per_usd} = USD 1.00</p>
              </div>
            )}
            {bd.us && (
              <div className="border rounded-lg p-5 bg-white" data-testid="bank-us">
                <div className="flex justify-between items-start mb-3">
                  <h3 className="font-semibold text-lg">Pay in USD from the United States</h3>
                  <Badge className="bg-blue-100 text-blue-800">USD ${(bd.amount_usd || order.amount).toFixed(2)}</Badge>
                </div>
                <BankRow label="Bank Name" value={bd.us.bank_name} />
                <BankRow label="Account Name" value={bd.us.account_name} />
                <BankRow label="Account Number" value={bd.us.account_number} onCopy={copy} />
                <BankRow label="Direct Deposit & Electronic Payment (ACH)" value={bd.us.ach_routing} onCopy={copy} />
                <BankRow label="Domestic Wire Transfers" value={bd.us.domestic_wire_routing} onCopy={copy} />
                <BankRow label="International Wire (SWIFT)" value={bd.us.swift} onCopy={copy} />
                <BankRow label="Reference" value={`Order ${orderShort}`} onCopy={copy} highlight />
              </div>
            )}
          </CardContent>
        </Card>

        {/* CTA */}
        <Card>
          <CardContent className="p-6">
            <h3 className="font-heading font-semibold text-lg mb-2">Already paid? Submit your proof of payment</h3>
            <p className="text-sm text-muted-foreground mb-4">Once we receive and verify your payment, your course access will be activated.</p>
            <Link to={`/orders/${order.id}/payment-proof?token=${token}`}>
              <Button className="bg-brand hover:bg-brand-dark text-white" data-testid="goto-proof-btn">
                Submit Proof of Payment <ChevronRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <p className="text-xs text-muted-foreground mt-4">
              View your order status anytime: <Link to={`/orders/${order.id}?token=${token}`} className="text-brand hover:underline">My Order Portal</Link>
            </p>
          </CardContent>
        </Card>

        <p className="text-center text-sm text-muted-foreground">
          Questions? <a href="mailto:admin@comply-cms.com" className="text-brand hover:underline">admin@comply-cms.com</a>
        </p>
      </div>
    </div>
  );
};

const BankRow = ({ label, value, onCopy, highlight }) => (
  <div className={`flex items-center justify-between py-2 border-b last:border-b-0 text-sm ${highlight ? "bg-yellow-50 px-2 -mx-2 rounded" : ""}`}>
    <span className="text-muted-foreground">{label}</span>
    <span className="flex items-center gap-2">
      <span className={`font-mono ${highlight ? "font-semibold" : ""}`}>{value || "—"}</span>
      {onCopy && value && (
        <button type="button" onClick={() => onCopy(value, label)} className="text-brand hover:text-brand-dark">
          <Copy className="h-3.5 w-3.5" />
        </button>
      )}
    </span>
  </div>
);

export default BankTransferConfirmation;
